import { FC, ReactNode } from "react";
import { Link } from "react-router-dom";
import { DataTable, DataTableColumn, DataTableEntityCell } from "../../../../../design-system";
import { Tactic } from "../../../../../types/TacticTypes";
import { DateTime } from "../../../../../common/components/DateTime";
import {
    rfxStatusPresentation,
    StatusProgressCell,
} from "../../../../project/components/ProjectStatusCell";

interface RfxTableProps {
    tactics: Tactic[];
    /** Controls first in the table's toolbar (the Deals / RFXs switch). */
    controls?: ReactNode;
    emptyText?: ReactNode;
}

const rfxPath = (tactic: Tactic) => `/project/${tactic.projectId}`;

const rfxStatus = (tactic: Tactic) => rfxStatusPresentation(tactic.status);

// The Status filter's choices, in pipeline order.
const STATUS_OPTIONS = ["Not started", "In progress", "Select vendor", "Complete"].map((value) => ({
    value,
    label: value,
}));

/** The negotiations view's RFx list. */
export const RfxTable: FC<RfxTableProps> = ({ tactics, controls, emptyText }) => {
    const columns: DataTableColumn<Tactic>[] = [
        {
            key: "rfx",
            title: "RFx",
            width: "20%",
            sort: "text",
            sortValue: (tactic) => tactic.name,
            render: (tactic) => (
                <DataTableEntityCell
                    imageFallback={(tactic.name || "?").charAt(0).toUpperCase()}
                    name={
                        tactic.projectId ? (
                            <Link to={rfxPath(tactic)}>{tactic.name}</Link>
                        ) : (
                            tactic.name
                        )
                    }
                    detail="RFx"
                />
            ),
        },
        {
            key: "status",
            title: "Status",
            // Pipeline order: not started, in progress, select vendor, complete.
            sort: "number",
            sortValue: (tactic) => rfxStatus(tactic)?.stage,
            filter: {
                type: "enum",
                options: STATUS_OPTIONS,
                value: (tactic) => rfxStatus(tactic)?.label,
            },
            render: (tactic) => <StatusProgressCell status={rfxStatus(tactic)} />,
        },
        {
            key: "created",
            title: "Date created",
            sort: "date",
            sortValue: (tactic) => tactic.recordCreationDate,
            render: (tactic) => <DateTime date={tactic.recordCreationDate} showTime={false} />,
        },
    ];

    return (
        <DataTable<Tactic>
            columns={columns}
            rows={tactics}
            rowKey={(tactic) => tactic.id}
            controls={controls}
            searchText={(tactic) => tactic.name}
            searchPlaceholder="Search RFXs"
            emptyText={emptyText}
            storageKey="rfx"
        />
    );
};
